import React from 'react';
import { Ship, Anchor, Truck, CheckCircle, Package, TrainFront } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';

const STEP_DEFS = [
  { key: 'origin', tLabel: 'shipments.journeyOrigin', tShort: 'shipments.journeyOriginShort', icon: Package, statuses: ['NO_SALIDO_CHINA', 'BOOKING_NO_SALIDO'] },
  { key: 'ocean', tLabel: 'shipments.journeyTransit', tShort: 'shipments.journeyTransitShort', icon: Ship, statuses: ['EN_TRANSITO', 'EN_TRANSITO_MARITIMO', 'EN_TRANSITO_PUERTO'] },
  { key: 'port', tLabel: 'shipments.journeyPort', tShort: 'shipments.journeyPortShort', icon: Anchor, statuses: ['ARRIBADO_PUERTO', 'EN_PUERTO_LIBERACION', 'EN_PUERTO_LIBERADO', 'EN_PUERTO_SEMAFORO_ROJO', 'EN_PUERTO_CARGA_FERRO'] },
  { key: 'land', tLabel: 'shipments.journeyLand', tShort: 'shipments.journeyLandShort', icon: Truck, statuses: ['EN_TRANSITO_TERRESTRE', 'EN_PANTACO', 'EN_DESTINO'] },
  { key: 'delivered', tLabel: 'shipments.journeyDelivered', tShort: 'shipments.journeyDeliveredShort', icon: CheckCircle, statuses: ['ENTREGADO', 'ENTREGADO_PEND_DEMORAS'] },
];

function getActiveStep(statusCode) {
  for (let i = STEP_DEFS.length - 1; i >= 0; i--) {
    if (STEP_DEFS[i].statuses.includes(statusCode)) return i;
  }
  return 0;
}

export function ShipmentJourney({ statusCode, eta, arrivedAt, deliveredAt, metodoTransporte }) {
  const { t } = useI18n();
  const activeStep = getActiveStep(statusCode || 'NO_SALIDO_CHINA');

  return (
    <div className="bg-card border border-border rounded-lg p-4" data-testid="shipment-journey">
      <div className="flex items-center justify-between relative">
        {/* Connection line */}
        <div className="absolute top-5 left-8 right-8 h-0.5 bg-border z-0" />
        <div
          className="absolute top-5 left-8 h-0.5 bg-primary z-0 transition-all duration-500"
          style={{ width: `${Math.min(100, (activeStep / (STEP_DEFS.length - 1)) * 100)}%`, maxWidth: 'calc(100% - 64px)' }}
        />

        {STEP_DEFS.map((step, i) => {
          // Dynamic icon for land transport: Train if "tren", Truck otherwise
          let Icon = step.icon;
          let label = t(step.tLabel);
          let shortLabel = t(step.tShort);
          if (step.key === 'land' && metodoTransporte === 'tren') {
            Icon = TrainFront;
            label = t('shipments.journeyTrain');
            shortLabel = t('shipments.journeyTrainShort');
          }

          const isActive = i === activeStep;
          const isPast = i < activeStep;
          const isFuture = i > activeStep;

          return (
            <div key={step.key} className="flex flex-col items-center z-10 relative" style={{ flex: 1 }}>
              <div
                className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-all duration-300
                  ${isActive ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/30 scale-110' :
                    isPast ? 'bg-primary/80 text-primary-foreground' :
                    'bg-muted text-muted-foreground'}`}
              >
                <Icon size={16} />
              </div>
              <span className={`text-[10px] sm:text-xs mt-2 text-center font-medium leading-tight
                ${isActive ? 'text-primary' : isPast ? 'text-foreground' : 'text-muted-foreground'}`}>
                <span className="hidden sm:inline">{label}</span>
                <span className="sm:hidden">{shortLabel}</span>
              </span>
              {/* Show date under active/past steps */}
              {step.key === 'port' && arrivedAt && (isPast || isActive) && (
                <span className="text-[10px] text-muted-foreground mt-0.5">{arrivedAt.slice(0, 10)}</span>
              )}
              {step.key === 'delivered' && deliveredAt && isPast && (
                <span className="text-[10px] text-muted-foreground mt-0.5">{deliveredAt.slice(0, 10)}</span>
              )}
              {step.key === 'ocean' && eta && isFuture && (
                <span className="text-[10px] text-muted-foreground mt-0.5">ETA: {eta.slice(0, 10)}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
